/**
 * Tax and compliance — the 194-O register, its deposits, and GST.
 *
 * **Nothing on this screen holds its own copy of a tax fact.** Which rates are live and unsigned,
 * what a quarter nets to, how much is still un-deposited, whether Section 52 TCS is switched on
 * — every one of them arrives from the server. A client copy of a server number has been wrong
 * three times in this console, and here being wrong means a return filed on a figure nobody
 * computed.
 *
 * **A rate is a row, and it arrives unconfirmed.** We do not invent tax positions; we record the
 * ones the accountant gave us, with the source note that says where the number came from. The
 * banner listing live unsigned rates is the launch gate's own answer, sent down rather than
 * filtered here.
 *
 * **A provider with no PAN is deducted at Section 206AA's higher rate.** That is the default
 * until somebody checks their card, and it is money they are not getting for a reason they can
 * fix — so the register says so rather than burying it.
 */
import { useState } from 'react';
import { StatusChip } from '../components/primitives';
import { Input, Select } from '../components/forms';
import { DataTable } from '../components/data';
import { EmptyState, ErrorState, LoadingState } from '../components/feedback';
import { ConfirmAction, FilterBar, Money, ScopeAction, Section } from '../components/kit';
import { useConsole } from '../app/console';
import { errorMessage } from '../lib/api/client';
import { parsePaise } from '../lib/money';
import { istMonth } from '../lib/datetime';
import { newIdempotencyKey } from '../lib/api/idempotency';
import {
  useAddRate,
  useChallans,
  useConfirmRate,
  useGenerateCertificates,
  useGstRegister,
  useRecordChallan,
  useRecordTdsFiling,
  useTdsFilings,
  useRecordGstRemittance,
  useReturnExport,
  useTaxRates,
  useTdsRegister,
  type TaxRateRow,
} from '../lib/api/hooks/tax';
import { TaxDocuments } from './TaxDocuments';
import { useUrlTab } from '../lib/filterCodecs';

const TABS = [
  { key: 'rates', label: 'Rate registry' },
  { key: 'tds', label: '194-O register' },
  { key: 'challans', label: 'Challans' },
  { key: 'returns', label: 'Returns' },
  { key: 'certs', label: 'Certificates' },
  { key: 'gst', label: 'GST & TCS' },
  { key: 'docs', label: 'Documents' },
];

/** The tab rides the URL (doc 05-admin/04 §3.3), so a pasted link opens the same tab. */
const TAB_KEYS = TABS.map((t) => t.key);

const PAGE_SIZE = 50;
const ALL = 'the whole year';
const QUARTERS = [ALL, 'Q1', 'Q2', 'Q3', 'Q4'];

/** The server's vocabulary, not a friendlier one — ops read the same words in the logs. */
const RATE_TYPES = [
  'tds_194o',
  'tds_194o_no_pan',
  'gst_standard',
  'gst_tcs_cgst',
  'gst_tcs_sgst',
  'gst_tcs_igst',
];

/** `2026-27`. Derived from today rather than typed, and the Indian year turns on 1 April. */
export function currentFinancialYear(today = new Date()): string {
  const start = today.getMonth() + 1 >= 4 ? today.getFullYear() : today.getFullYear() - 1;
  return `${start}-${String(start + 1).slice(-2)}`;
}

/** `2026-08` — the GST return month. */
export function currentReturnPeriod(today = new Date()): string {
  return `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}`;
}

export function recordTone(recordType: string): 'success' | 'warn' | 'error' | 'neutral' {
  if (recordType === 'reversal') return 'error';
  if (recordType === 'correction') return 'warn';
  return 'neutral';
}

export function Tax() {
  const { scope, toast } = useConsole();
  const [tab, setTab] = useUrlTab('tax', TAB_KEYS, 'rates');
  const [fy, setFy] = useState(currentFinancialYear());
  const [quarter, setQuarter] = useState(ALL);
  const [period, setPeriod] = useState(currentReturnPeriod());
  const [onlyUndeposited, setOnlyUndeposited] = useState(false);
  const [showTcs, setShowTcs] = useState(false);
  const [dialog, setDialog] = useState<string | null>(null);
  const [key, setKey] = useState('');
  const [confirming, setConfirming] = useState<TaxRateRow | null>(null);

  /* New-rate form state. Held here rather than in the dialog so a mistyped window is not lost
     when the operator closes it to check the notification. */
  const [rateType, setRateType] = useState(RATE_TYPES[0]!);
  const [rate, setRate] = useState('0.0010');
  const [from, setFrom] = useState('');
  /* Empty means "the latest version's basis", which the server fills in. */
  const [basis, setBasis] = useState('');
  const [challanNumber, setChallanNumber] = useState('');
  const [bsr, setBsr] = useState('');
  const [depositDate, setDepositDate] = useState('');
  const [depositMonth, setDepositMonth] = useState('');
  const [reference, setReference] = useState('');
  const [amount, setAmount] = useState('');
  const [filingQuarter, setFilingQuarter] = useState('Q1');
  const [filingKind, setFilingKind] = useState('original');
  const [ackNo, setAckNo] = useState('');
  const [filedOn, setFiledOn] = useState('');

  const rates = useTaxRates();
  /* The bases the registry already records. The server's list is the authority: anything else is
     refused 422 `unknown_basis`, naming the accepted ones, and shown as sent. */
  const bases = [
    ...new Set((rates.data?.items ?? []).flatMap((r) => (r.basis ? [r.basis] : []))),
  ].sort();
  const register = useTdsRegister({
    fy,
    page: 1,
    page_size: PAGE_SIZE,
    ...(quarter === ALL ? {} : { quarter }),
    ...(onlyUndeposited ? { undeposited: true } : {}),
  });
  /* A deposit reads the undeposited deductions itself rather than the register on screen, which is
     filtered however it was left: a row a challan already covers is never offered again. */
  /* 100 is the server's page ceiling: a month past it is deposited in two challans. */
  const pending = useTdsRegister({ fy, page: 1, page_size: 100, undeposited: true });
  const challans = useChallans(fy, tab === 'challans');
  const filings = useTdsFilings(fy, tab === 'returns');
  const gst = useGstRegister(period, showTcs, tab === 'gst');
  const addRate = useAddRate();
  const confirmRate = useConfirmRate();
  const recordChallan = useRecordChallan();
  const recordFiling = useRecordTdsFiling();
  const generate = useGenerateCertificates();
  const remit = useRecordGstRemittance();
  const exportReturn = useReturnExport();

  const fail = (error: unknown) => toast({ tone: 'error', message: errorMessage(error) });
  const openDialog = (which: string) => {
    setKey(newIdempotencyKey());
    setDialog(which);
  };

  /* One challan discharges one IST month — that month is its `period_month`, not the day it was
     paid. The oldest month comes first: its deadline is the nearest. */
  const pendingRows = pending.data?.items ?? [];
  const months = [...new Set(pendingRows.map((r) => istMonth(r.created_at)))].sort();
  const month = months.includes(depositMonth) ? depositMonth : (months[0] ?? '');
  const outstanding = pendingRows.filter((r) => istMonth(r.created_at) === month);

  return (
    <>
      <FilterBar tabs={TABS} tab={tab} onTab={setTab} />

      {/* The launch gate's own answer, sent down rather than worked out here. */}
      {rates.isSuccess && rates.data.unconfirmed_in_force.length > 0 && (
        <Section
          title="Rates in force that nobody has signed off"
          note="Indicative until the accountant confirms them — a seed quietly becoming production is what this list exists to prevent"
        >
          <DataTable
            density="compact"
            columns={[
              { key: 'type', label: 'Rate' },
              { key: 'rate', label: 'Fraction', align: 'right', mono: true },
              { key: 'from', label: 'From' },
              { key: 'source', label: 'Where it came from' },
            ]}
            rows={rates.data.unconfirmed_in_force.map((r) => ({
              id: r.id,
              type: r.rate_type,
              rate: r.rate,
              from: r.effective_from,
              source: r.source_note,
            }))}
          />
        </Section>
      )}

      {tab === 'rates' && (
        <Section
          title="Rate registry"
          note="A rate is a row with a window. A change is a new version; the open one is closed the day before it starts."
          actions={
            <ScopeAction
              scope={scope}
              need="finance"
              size="sm"
              variant="secondary"
              onClick={() => openDialog('rate')}
            >
              New rate version
            </ScopeAction>
          }
        >
          {rates.isPending && <LoadingState message="Reading the registry…" />}
          {rates.isError && (
            <ErrorState title="Could not read the registry" cause={errorMessage(rates.error)} />
          )}
          {rates.isSuccess && (
            <DataTable
              density="compact"
              columns={[
                { key: 'type', label: 'Rate' },
                { key: 'rate', label: 'Fraction', align: 'right', mono: true },
                { key: 'basis', label: 'Basis', mono: true },
                { key: 'window', label: 'In force' },
                { key: 'source', label: 'Where it came from' },
                { key: 'state', label: 'Signed off' },
                { key: 'act', label: '' },
              ]}
              rows={rates.data.items.map((r) => ({
                id: r.id,
                type: r.rate_type,
                /* A fraction, exactly as stored. `0.0010` is 0.1% — showing a percentage here
                   would be this screen doing arithmetic on a statutory number. */
                rate: r.rate,
                basis: r.basis ?? 'not recorded',
                window: `${r.effective_from} → ${r.effective_to ?? 'open'}`,
                source: r.source_note,
                state: r.is_confirmed ? (
                  <StatusChip tone="success" label="confirmed" />
                ) : (
                  <StatusChip tone="warn" label="unconfirmed" />
                ),
                act: r.is_confirmed ? null : (
                  <ScopeAction
                    scope={scope}
                    need="finance"
                    size="sm"
                    variant="ghost"
                    onClick={() => {
                      setConfirming(r);
                      openDialog('confirm');
                    }}
                  >
                    Record CA sign-off
                  </ScopeAction>
                ),
              }))}
            />
          )}
        </Section>
      )}

      {tab === 'tds' && (
        <Section
          title="Section 194-O register"
          note="Deductions and reversals. The net is what is actually owed for the period — computed on the server, over the whole period rather than the page."
          actions={
            <>
              <ScopeAction
                scope={scope}
                need="finance"
                size="sm"
                variant="ghost"
                onClick={() => setOnlyUndeposited(!onlyUndeposited)}
              >
                {onlyUndeposited ? 'Show everything' : 'Only what is not deposited'}
              </ScopeAction>
              <ScopeAction
                scope={scope}
                need="finance"
                size="sm"
                variant="ghost"
                disabled={quarter === ALL}
                onClick={() =>
                  exportReturn.mutate(
                    { fy, quarter },
                    {
                      onSuccess: (csv) => {
                        /* Handed to the operator as a file: it is what the accountant files
                           from, and a table on a screen is not that. */
                        const url = URL.createObjectURL(
                          new Blob([csv], { type: 'text/csv' }),
                        );
                        const link = document.createElement('a');
                        link.href = url;
                        link.download = `tds-${fy}-${quarter}.csv`;
                        link.click();
                        URL.revokeObjectURL(url);
                        toast({ tone: 'success', message: 'Return exported' });
                      },
                      onError: fail,
                    },
                  )
                }
              >
                Export the return
              </ScopeAction>
            </>
          }
        >
          <FilterBar
            extra={
              <>
                <Input label="Financial year" value={fy} onChange={setFy} />
                <Select
                  label="Quarter"
                  value={quarter}
                  options={QUARTERS}
                  onChange={setQuarter}
                />
              </>
            }
          />
          {register.isPending && <LoadingState message="Reading the register…" />}
          {register.isError && (
            <ErrorState
              title="Could not read the register"
              cause={errorMessage(register.error)}
            />
          )}
          {register.isSuccess && register.data.items.length === 0 && (
            <EmptyState
              title="Nothing deducted in this period"
              body="A deduction is written when a booking is recognised — at completion, with the payable it comes out of."
            />
          )}
          {register.isSuccess && register.data.items.length > 0 && (
            <DataTable
              density="compact"
              columns={[
                { key: 'kind', label: 'Kind' },
                { key: 'pandit', label: 'Provider', mono: true },
                { key: 'gross', label: 'Gross', align: 'right', mono: true },
                { key: 'tds', label: 'Deducted', align: 'right', mono: true },
                { key: 'period', label: 'Period' },
                { key: 'flag', label: '' },
              ]}
              rows={register.data.items.map((r) => ({
                id: r.id,
                kind: <StatusChip tone={recordTone(r.record_type)} label={r.record_type} />,
                pandit: `#${r.pandit_profile_id}`,
                gross: <Money paise={r.gross_amount_paise} showPaise />,
                tds: <Money paise={r.tds_paise} showPaise />,
                period: `${r.financial_year} ${r.quarter}`,
                /* A reversal after its quarter was filed cannot change that return; the fix is a
                   correction statement with the tax office, and this is that queue. */
                flag: r.needs_return_correction ? (
                  <StatusChip tone="error" label="needs a return correction" />
                ) : null,
              }))}
              footer={
                <>
                  <span>
                    {register.data.total} row{register.data.total === 1 ? '' : 's'} · net{' '}
                    {register.data.net_tds_paise} paise owed
                  </span>
                  <span>{register.data.undeposited_total_paise} paise not yet deposited</span>
                </>
              }
            />
          )}
        </Section>
      )}

      {tab === 'challans' && (
        <Section
          title="Deposits"
          note="One challan covers many deductions, and a deduction can be covered exactly once."
          actions={
            <ScopeAction
              scope={scope}
              need="finance"
              size="sm"
              variant="secondary"
              disabled={outstanding.length === 0}
              onClick={() => openDialog('challan')}
            >
              Record a deposit
            </ScopeAction>
          }
        >
          {challans.isPending && <LoadingState message="Reading the deposits…" />}
          {challans.isError && (
            <ErrorState title="Could not read the deposits" cause={errorMessage(challans.error)} />
          )}
          {challans.isSuccess && challans.data.length === 0 && (
            <EmptyState
              title="No deposit recorded this year"
              body="The month cannot be closed while a deduction sits un-deposited, so this list catching up is the point."
            />
          )}
          {challans.isSuccess && challans.data.length > 0 && (
            <DataTable
              density="compact"
              columns={[
                { key: 'cin', label: 'Challan', mono: true },
                { key: 'bsr', label: 'BSR' },
                { key: 'on', label: 'Deposited' },
                { key: 'month', label: 'Discharges' },
                { key: 'total', label: 'Amount', align: 'right', mono: true },
              ]}
              rows={challans.data.map((c) => ({
                id: c.id,
                cin: c.challan_number,
                bsr: c.bsr_code ?? '—',
                on: c.deposit_date,
                month: c.period_month,
                total: <Money paise={c.total_paise} showPaise />,
              }))}
            />
          )}
        </Section>
      )}

      {tab === 'returns' && (
        <Section
          title="Quarterly returns"
          note="Recorded after the return is filed on the portal. A quarter is refused while any deduction in it has no challan."
          actions={
            <ScopeAction
              scope={scope}
              need="finance"
              size="sm"
              variant="secondary"
              onClick={() => openDialog('filing')}
            >
              Record a filing
            </ScopeAction>
          }
        >
          {filings.isPending && <LoadingState message="Reading the returns…" />}
          {filings.isError && (
            <ErrorState title="Could not read the returns" cause={errorMessage(filings.error)} />
          )}
          {filings.isSuccess && filings.data.length === 0 && (
            <EmptyState
              title="No return recorded this year"
              body="A quarter's 26Q is due by the end of the following month; until it is recorded here, nothing in the console knows it was filed."
            />
          )}
          {filings.isSuccess && filings.data.length > 0 && (
            <DataTable
              density="compact"
              columns={[
                { key: 'quarter', label: 'Quarter' },
                { key: 'form', label: 'Form' },
                { key: 'ack', label: 'Acknowledgement', mono: true },
                { key: 'on', label: 'Filed on' },
                { key: 'kind', label: 'Kind' },
                { key: 'by', label: 'Recorded by' },
              ]}
              rows={filings.data.map((f) => ({
                id: f.id,
                quarter: f.quarter,
                form: f.form,
                ack: f.acknowledgement_no,
                on: f.filed_on,
                kind: f.is_correction ? 'correction' : 'original',
                by: `admin #${f.filed_by_admin_id}`,
              }))}
            />
          )}
        </Section>
      )}

      {tab === 'certs' && (
        <Section
          title="Provider certificates"
          note="One per provider per quarter, generated from the register. A provider who already has one is skipped rather than reissued — the number they may have filed against stays theirs."
          actions={
            <ScopeAction
              scope={scope}
              need="finance"
              size="sm"
              variant="secondary"
              disabled={quarter === ALL}
              onClick={() => openDialog('certs')}
            >
              Generate for {quarter === ALL ? 'a quarter' : quarter}
            </ScopeAction>
          }
        >
          {generate.isSuccess ? (
            <DataTable
              density="compact"
              columns={[
                { key: 'number', label: 'Certificate', mono: true },
                { key: 'pandit', label: 'Provider' },
                { key: 'gross', label: 'Gross', align: 'right', mono: true },
                { key: 'tds', label: 'Deducted', align: 'right', mono: true },
              ]}
              rows={generate.data.issued.map((c) => ({
                id: c.id,
                number: c.certificate_number,
                pandit: `#${c.pandit_profile_id}`,
                gross: <Money paise={c.total_gross_paise} showPaise />,
                tds: <Money paise={c.total_tds_paise} showPaise />,
              }))}
              footer={
                <>
                  <span>{generate.data.issued.length} issued</span>
                  <span>
                    {generate.data.skipped_pandit_profile_ids.length} already had one and were
                    skipped
                  </span>
                </>
              }
            />
          ) : (
            <EmptyState
              title="No run yet"
              body="Pick a quarter on the register tab, then generate. The PDF itself is rendered by the quarterly job, so nothing here offers a download until the file exists."
            />
          )}
        </Section>
      )}

      {tab === 'docs' && <TaxDocuments initialFy={fy} />}

      {tab === 'gst' && (
        <Section
          title="GST"
          note="Our own supplies — commission and the platform fee. Section 52 collection has its own register because it has its own return."
          actions={
            <ScopeAction
              scope={scope}
              need="finance"
              size="sm"
              variant="secondary"
              onClick={() => openDialog('remittance')}
            >
              Record a remittance
            </ScopeAction>
          }
        >
          <FilterBar
            extra={
              <>
                <Input label="Return period" value={period} onChange={setPeriod} />
                <ScopeAction
                  scope={scope}
                  need="finance"
                  size="sm"
                  variant="ghost"
                  onClick={() => setShowTcs(!showTcs)}
                >
                  {showTcs ? 'Show our own supplies' : 'Show Section 52 collection'}
                </ScopeAction>
              </>
            }
          />
          {gst.isPending && <LoadingState message="Reading the register…" />}
          {gst.isError && (
            <ErrorState title="Could not read the register" cause={errorMessage(gst.error)} />
          )}
          {gst.isSuccess && (
            <>
              {/* Read from the response, never assumed: empty and dormant look identical, and an
                  operator needs to know which one they are looking at. */}
              {!gst.data.tcs_enabled && (
                <EmptyState
                  title="Section 52 collection is switched off"
                  body="Whether TCS applies to us is still an open question for the accountant, and collecting a tax we are not required to collect is as wrong as failing to collect one we are."
                />
              )}
              <DataTable
                density="compact"
                columns={[
                  { key: 'supply', label: 'Supply' },
                  { key: 'taxable', label: 'Taxable', align: 'right', mono: true },
                  { key: 'cgst', label: 'CGST', align: 'right', mono: true },
                  { key: 'sgst', label: 'SGST', align: 'right', mono: true },
                  { key: 'igst', label: 'IGST', align: 'right', mono: true },
                  { key: 'pos', label: 'Place' },
                ]}
                rows={gst.data.items.map((g) => ({
                  id: g.id,
                  supply: g.supply_type,
                  taxable: <Money paise={g.taxable_value_paise} showPaise />,
                  cgst: <Money paise={g.cgst_paise} showPaise />,
                  sgst: <Money paise={g.sgst_paise} showPaise />,
                  igst: <Money paise={g.igst_paise} showPaise />,
                  pos: g.place_of_supply,
                }))}
                footer={
                  <>
                    <span>{gst.data.total} supplies</span>
                    <span>{gst.data.taxable_value_paise} paise taxable</span>
                  </>
                }
              />
            </>
          )}
        </Section>
      )}

      {dialog === 'rate' && (
        <ConfirmAction
          open
          title="Register a new rate version?"
          confirmLabel="Register"
          phrase="RATE"
          consequences={[
            'Nothing is written until a second finance admin approves it on Approvals — never the person asking.',
            'Every booking made from its start date is rated against this version.',
            'It arrives unconfirmed, and stays that way until somebody records the accountant’s sign-off.',
            'If a version is already open, it is closed the day before this one starts.',
          ]}
          reason="Why — this is written down"
          onCancel={() => setDialog(null)}
          onConfirm={(why) =>
            addRate.mutate(
              {
                rate_type: rateType,
                rate,
                ...(basis === '' ? {} : { basis }),
                effective_from: from,
                source_note: reference,
                reason: why,
              },
              {
                onSuccess: () => {
                  setDialog(null);
                  toast({ tone: 'success', message: 'Rate sent for a second approval' });
                },
                onError: (error) => {
                  setDialog(null);
                  fail(error);
                },
              },
            )
          }
        >
          <Select label="Rate" value={rateType} options={RATE_TYPES} onChange={setRateType} />
          <Input
            label="Fraction"
            value={rate}
            onChange={setRate}
            helper="0.0010 is 0.1%. A fraction, never a percentage — the ambiguity is how a tax bill gets multiplied by a hundred."
          />
          <Select
            label="Charged on"
            value={basis}
            options={bases}
            onChange={setBasis}
            placeholder="Same basis as the latest version"
          />
          <Input label="In force from" value={from} onChange={setFrom} placeholder="2026-10-01" />
          <Input
            label="Where the number came from"
            value={reference}
            onChange={setReference}
            helper="A section, a notification, a circular. A rate nobody can source is one nobody can defend."
          />
        </ConfirmAction>
      )}

      {dialog === 'confirm' && confirming !== null && (
        <ConfirmAction
          open
          title={`Record sign-off for ${confirming.rate_type}?`}
          confirmLabel="Record sign-off"
          phrase="CONFIRM"
          consequences={[
            'This says the accountant has seen and agreed this rate.',
            'The reference is added to the source note; where the number originally came from is kept.',
            'A second finance admin approves it before the rate counts as confirmed.',
          ]}
          reason="Why — this is written down"
          onCancel={() => setDialog(null)}
          onConfirm={(why) =>
            confirmRate.mutate(
              { rateId: confirming.id, sourceNote: reference, reason: why },
              {
                onSuccess: () => {
                  setDialog(null);
                  toast({ tone: 'success', message: 'Sign-off sent for a second approval' });
                },
                onError: (error) => {
                  setDialog(null);
                  fail(error);
                },
              },
            )
          }
        >
          <Input
            label="The accountant's reference"
            value={reference}
            onChange={setReference}
            placeholder="CA sign-off 2026-04-05"
          />
        </ConfirmAction>
      )}

      {dialog === 'challan' && (
        <ConfirmAction
          open
          title="Record this deposit?"
          confirmLabel="Record"
          phrase="RECORD"
          tone="error"
          consequences={[
            'Nothing is written until a second finance admin approves it on Approvals — never the person asking.',
            `It discharges ${outstanding.length} deduction${outstanding.length === 1 ? '' : 's'} made in ${month}.`,
            'A deduction covered by one challan cannot be covered by another.',
            'The ledger debit is split per provider, so each one’s balance moves with it.',
          ]}
          reason="Why — this is written down"
          onCancel={() => setDialog(null)}
          onConfirm={(why) =>
            recordChallan.mutate(
              {
                key,
                challan_number: challanNumber,
                bsr_code: bsr || null,
                bank_reference: null,
                deposit_date: depositDate,
                period_month: `${month}-01`,
                financial_year: fy,
                total_paise: outstanding.reduce((sum, r) => sum + r.tds_paise, 0),
                allocations: outstanding.map((r) => ({
                  tds_record_id: r.id,
                  amount_paise: r.tds_paise,
                })),
                reason: why,
              },
              {
                onSuccess: () => {
                  setDialog(null);
                  toast({ tone: 'success', message: 'Deposit sent for a second approval' });
                },
                onError: (error) => {
                  setDialog(null);
                  fail(error);
                },
              },
            )
          }
        >
          <Select
            label="Month being discharged"
            value={month}
            options={months}
            onChange={setDepositMonth}
          />
          <Input
            label="Challan number"
            value={challanNumber}
            onChange={setChallanNumber}
            helper="The CIN from the bank. It is unique — two rows for one deposit would double what reconciliation believes was paid."
          />
          <Input label="BSR code" value={bsr} onChange={setBsr} />
          <Input
            label="Deposited on"
            value={depositDate}
            onChange={setDepositDate}
            placeholder="2026-09-07"
          />
        </ConfirmAction>
      )}

      {dialog === 'filing' && (
        <ConfirmAction
          open
          title={`Record the ${filingQuarter} return for ${fy}?`}
          confirmLabel="Record"
          phrase="RECORD"
          consequences={[
            'This records a return already filed on the portal; it files nothing itself.',
            'The server refuses it while any deduction in the quarter has no challan.',
          ]}
          reason="Why — this is written down"
          onCancel={() => setDialog(null)}
          onConfirm={(why) =>
            recordFiling.mutate(
              {
                key,
                financial_year: fy,
                quarter: filingQuarter,
                acknowledgement_no: ackNo.trim(),
                filed_on: filedOn,
                is_correction: filingKind === 'correction',
                reason: why,
              },
              {
                onSuccess: () => {
                  setDialog(null);
                  toast({ tone: 'success', message: `${filingQuarter} return recorded` });
                },
                onError: (error) => {
                  setDialog(null);
                  fail(error);
                },
              },
            )
          }
        >
          <Select
            label="Quarter"
            value={filingQuarter}
            onChange={setFilingQuarter}
            options={['Q1', 'Q2', 'Q3', 'Q4']}
          />
          <Select
            label="Kind"
            value={filingKind}
            onChange={setFilingKind}
            options={['original', 'correction']}
          />
          <Input
            label="Acknowledgement number"
            value={ackNo}
            onChange={setAckNo}
            helper="The token number the portal issued when the return was accepted."
          />
          <Input label="Filed on" value={filedOn} onChange={setFiledOn} placeholder="2026-07-28" />
        </ConfirmAction>
      )}

      {dialog === 'certs' && (
        <ConfirmAction
          open
          title={`Generate ${quarter} certificates?`}
          confirmLabel="Generate"
          phrase="GENERATE"
          consequences={[
            'One certificate per provider, from the register as it stands now.',
            'A provider who already has one for this quarter is skipped, not reissued.',
          ]}
          onCancel={() => setDialog(null)}
          onConfirm={() =>
            generate.mutate(
              { fy, quarter, key },
              {
                onSuccess: (batch) => {
                  setDialog(null);
                  toast({
                    tone: 'success',
                    message: `${batch.issued.length} issued · ${batch.skipped_pandit_profile_ids.length} skipped`,
                  });
                },
                onError: (error) => {
                  setDialog(null);
                  fail(error);
                },
              },
            )
          }
        />
      )}

      {dialog === 'remittance' && (
        <ConfirmAction
          open
          title="Record a GST remittance?"
          confirmLabel="Record"
          phrase="REMIT"
          tone="error"
          consequences={[
            'Nothing is written until a second finance admin approves it on Approvals — never the person asking.',
            'This says the money has already gone to the government.',
            'It discharges the liability for the period you name, and nothing else.',
          ]}
          reason="Why — this is written down"
          onCancel={() => setDialog(null)}
          onConfirm={(why) =>
            remit.mutate(
              {
                key,
                tcs: false,
                return_period: period,
                amount_paise: parsePaise(amount) ?? 0,
                reference,
                remitted_on: depositDate,
                reason: why,
              },
              {
                onSuccess: () => {
                  setDialog(null);
                  toast({ tone: 'success', message: 'Remittance sent for a second approval' });
                },
                onError: (error) => {
                  setDialog(null);
                  fail(error);
                },
              },
            )
          }
        >
          <Input
            label="Amount"
            prefix="₹"
            value={amount}
            onChange={setAmount}
            inputMode="decimal"
            helper="Read digit by digit into paise. Never through a float — a lost paisa in a
              marketplace ledger is a reconciliation break somebody has to chase."
          />
          <Input label="Reference" value={reference} onChange={setReference} />
          <Input
            label="Remitted on"
            value={depositDate}
            onChange={setDepositDate}
            placeholder="2026-09-20"
          />
        </ConfirmAction>
      )}
    </>
  );
}
